import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { RefreshCw, Building2, CheckCircle, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://brown-pelican-168360.hostingersite.com/api';

const PendingStudios = () => {
    const [pending, setPending] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);

    const fetchPending = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API_URL}/admin/studios/pending`);
            setPending(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch pending studios:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchPending(); }, []);

    const handleApprove = async (id) => {
        if (!window.confirm('Approve this studio? They will be able to login immediately.')) return;
        setActionLoading(id);
        try {
            await axios.post(`${API_URL}/admin/studios/${id}/approve`);
            setPending(prev => prev.filter(s => s.id !== id));
        } catch (err) {
            alert('Failed to approve studio');
        } finally {
            setActionLoading(null);
        }
    };

    const handleReject = async (id) => {
        if (!window.confirm('Reject this registration? They will be marked as Removed.')) return;
        setActionLoading(id);
        try {
            const res = await axios.post(`${API_URL}/admin/studios/${id}/reject`);
            if (res.data.success) {
                alert('Success: Studio registration rejected.');
                setPending(prev => prev.filter(s => s.id !== id));
            } else {
                alert(`Error: ${res.data.message || 'Failed to reject'}`);
            }
        } catch (err) {
            console.error('Rejection error:', err);
            const msg = err.response?.data?.message || 'Failed to reject studio';
            alert(`Error: ${msg}`);
        } finally {
            setActionLoading(null);
        }
    };

    return (
        <div className="space-y-6 animate-fade-in-up">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight">Pending Approvals</h2>
                    <p className="text-sm text-slate-400 mt-1">Registrations awaiting review.</p>
                </div>
                <button onClick={fetchPending} className="w-full sm:w-auto bg-slate-800 border border-slate-700 text-slate-300 px-6 py-2.5 rounded-md font-bold text-xs flex items-center justify-center gap-2 hover:bg-slate-700 transition">
                    <RefreshCw size={14} /> REFRESH
                </button>
            </div>

            {/* Count Badge */}
            <div className="flex gap-4">
                <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 flex items-center gap-4 w-full sm:max-w-xs shadow-lg">
                    <div className="w-12 h-12 bg-amber-900/30 rounded-xl flex items-center justify-center shrink-0">
                        <Building2 size={24} className="text-amber-500" />
                    </div>
                    <div>
                        <div className="text-2xl font-black text-white">{pending.length}</div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Pending Reviews</div>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="bg-slate-800 border border-slate-700 rounded-xl p-12 text-center">
                    <div className="w-8 h-8 border-4 border-slate-600 border-t-[#5C5CFF] rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-slate-400 text-sm">Loading pending registrations...</p>
                </div>
            ) : pending.length === 0 ? (
                <div className="bg-slate-800 border border-slate-700 rounded-xl p-12 text-center">
                    <div className="w-16 h-16 bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle size={32} className="text-green-500" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">All Caught Up!</h3>
                    <p className="text-slate-400 text-sm">No pending studio registrations to review.</p>
                </div>
            ) : (
                <div className="bg-slate-800 border border-slate-700 rounded-xl shadow-sm overflow-x-auto custom-scrollbar no-scrollbar">
                    <table className="w-full text-left text-sm text-slate-300">
                        <thead className="bg-slate-900 text-xs uppercase font-bold text-slate-500 border-b border-slate-700">
                            <tr>
                                <th className="px-6 py-4">Studio</th>
                                <th className="px-6 py-4">Owner</th>
                                <th className="px-6 py-4">Email</th>
                                <th className="px-6 py-4">Registered</th>
                                <th className="px-6 py-4">Status</th>
                                <th className="px-6 py-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700">
                            {pending.map(s => (
                                <tr key={s.id} className="hover:bg-slate-700/50 group">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center font-bold text-xs bg-gradient-to-br from-amber-400 to-orange-500 text-white">
                                                {s.studio_name ? s.studio_name.substring(0, 2).toUpperCase() : 'ST'}
                                            </div>
                                            <div>
                                                <div className="font-bold text-white">{s.studio_name}</div>
                                                <div className="text-xs text-slate-500">ID: #{s.id}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 font-medium text-slate-400">{s.owner_name || '-'}</td>
                                    <td className="px-6 py-4 text-slate-500">{s.studio_email}</td>
                                    <td className="px-6 py-4 text-slate-500">
                                        {s.created_at ? new Date(s.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="bg-amber-900/30 text-amber-500 px-3 py-1 rounded-full text-xs font-bold">Pending</span>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex gap-2 justify-end">
                                            <button
                                                onClick={() => handleApprove(s.id)}
                                                disabled={actionLoading === s.id}
                                                className="bg-emerald-600 text-white px-4 py-2 rounded-md text-xs font-bold flex items-center gap-1.5 hover:bg-emerald-700 disabled:opacity-50 transition shadow-sm"
                                            >
                                                {actionLoading === s.id ? (
                                                    <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                ) : (
                                                    <CheckCircle size={14} />
                                                )}
                                                APPROVE
                                            </button>
                                            <button
                                                onClick={() => handleReject(s.id)}
                                                disabled={actionLoading === s.id}
                                                className="bg-red-900/50 text-red-400 border border-red-800 px-4 py-2 rounded-md text-xs font-bold flex items-center gap-1.5 hover:bg-red-900/70 disabled:opacity-50 transition"
                                            >
                                                <XCircle size={14} />
                                                REJECT
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default PendingStudios;
